import React, { useEffect, useState } from "react";
import { Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { listReviews, createReview } from "../api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return "";
  }
};

const Stars = ({ value, size = 15 }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} className={i < Math.round(value) ? "fill-[#c9a24b] text-[#c9a24b]" : "text-[#3a342b]"} />
    ))}
  </span>
);

const ProductReviews = ({ slug, onStats }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => {
    listReviews(slug)
      .then((data) => {
        setReviews(data.reviews || []);
        setStats({ count: data.count || 0, average: data.average || 0 });
        if (onStats) onStats({ count: data.count || 0, average: data.average || 0 });
      })
      .catch(() => {});
  };

  useEffect(() => {
    setShowForm(false);
    setName(""); setEmail(""); setComment(""); setRating(0); setHover(0);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please add your name");
    if (rating < 1) return toast.error("Please select a star rating");
    if (!comment.trim()) return toast.error("Please write a short review");
    setSubmitting(true);
    try {
      await createReview(slug, { name: name.trim(), rating, comment: comment.trim(), email: email.trim() || undefined });
      toast.success("Thank you!", { description: "Your review is now live." });
      setShowForm(false);
      setName(""); setEmail(""); setComment(""); setRating(0);
      refresh();
    } catch (err) {
      toast.error("Could not submit review", { description: "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-[1280px] mx-auto px-6 py-16 border-t border-[#2c2822]" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <h2 className="font-display text-[34px] md:text-[44px] text-[#f4efe4]">Customer Reviews</h2>
          <div className="flex items-center gap-3 mt-3">
            <Stars value={stats.average} size={18} />
            <span className="text-[#c9a24b] text-lg font-display">{stats.count ? stats.average.toFixed(1) : "—"}</span>
            <span className="text-[#a29a8c] text-sm">
              {stats.count ? `Based on ${stats.count} review${stats.count === 1 ? "" : "s"}` : "No reviews yet"}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="write-review-btn"
          className="btn-outline-gold px-7 py-3 rounded-full text-sm tracking-[0.15em] uppercase"
        >
          {showForm ? "Cancel" : "Write a Review"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#141210] border border-[#2c2822] rounded-2xl p-6 md:p-8 mb-12 max-w-2xl" data-testid="review-form">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[#a29a8c] text-sm">Your rating</span>
            <span className="inline-flex items-center gap-1" data-testid="review-rating-stars">
              {Array.from({ length: 5 }).map((_, i) => {
                const v = i + 1;
                return (
                  <button
                    type="button"
                    key={v}
                    onMouseEnter={() => setHover(v)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(v)}
                    data-testid={`review-star-${v}`}
                    className="p-0.5"
                    aria-label={`${v} star`}
                  >
                    <Star size={24} className={(hover || rating) >= v ? "fill-[#c9a24b] text-[#c9a24b]" : "text-[#3a342b] hover:text-[#c9a24b]/60"} />
                  </button>
                );
              })}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required
              data-testid="review-name-input"
              className="bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors"
            />
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional, not shown)"
              data-testid="review-email-input"
              className="bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors"
            />
          </div>
          <p className="text-[#6b6459] text-xs mb-4 -mt-1">Use the email from your order to earn a “Verified Purchase” badge.</p>
          <textarea
            value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience with this product…" rows={4} required
            data-testid="review-comment-input"
            className="w-full bg-[#0b0a09] border border-[#2c2822] rounded-md px-4 py-3 text-[#f4efe4] outline-none focus:border-[#c9a24b] transition-colors resize-none mb-5"
          />
          <button type="submit" disabled={submitting} data-testid="review-submit-btn" className="btn-gold px-8 py-3 rounded-full text-sm tracking-[0.2em] uppercase font-medium disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      )}

      {reviews.length === 0 ? (
        <p className="text-[#a29a8c]" data-testid="no-reviews">Be the first to review this product.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-[#141210] border border-[#2c2822] rounded-xl p-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-3">
                <Stars value={r.rating} />
                <span className="text-[#6b6459] text-xs">{fmtDate(r.createdAt)}</span>
              </div>
              <p className="text-[#c8c0b2] leading-relaxed">{r.comment}</p>
              <div className="flex items-center gap-2 mt-4">
                <p className="text-[#f4efe4] text-sm font-medium">— {r.name}</p>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[#c9a24b] text-[11px] tracking-[0.05em] uppercase" data-testid="verified-badge">
                    <ShieldCheck size={13} /> Verified Purchase
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
